import { useState, useEffect, useRef, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/integrations/supabase/client'
import { IS_DEMO_MODE } from '@/config/config'
import { useAuth } from '@/contexts/AuthContext'
import { Edit, FileText, Calendar, User, Users, Search, Filter, SortAsc, SortDesc, Trash2, Eye, EyeOff, RotateCcw } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { DeleteConfirmDialog } from '@/components/ui/ConfirmDialog'

const NOTE_TYPES = ['entretien', "réunion d’équipe", 'anamnèse', 'contacts réseau']
const capitalizeFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

const TYPE_COLORS: Record<string, string> = {
    'entretien': 'bg-blue-100 text-blue-700',
    "réunion d'équipe": 'bg-purple-100 text-purple-700',
    'anamnèse': 'bg-emerald-100 text-emerald-700',
    'contacts réseau': 'bg-orange-100 text-orange-700',
}

interface Note {
    id: string
    note_date: string
    note: string
    comment: string | null
    worker_name: string
    colleague_names: string[]
    created_at: string
    linked_exchange_id: string | null
    type: string | null
}

interface Worker {
    id: string
    name: string
}

interface TenantNotesProps {
    tenantId: string
    tenantName: string
    onNavigateBack: () => void
    onEditNote: (noteId: string, noteData: any) => void
    highlightNoteId?: string
    onNavigateToExchange?: (exchangeId: string, beneficiaryId: string) => void
}

const NOTES_PER_PAGE = 20
const FILTERED_NOTES_PER_PAGE = 100

export const TenantNotes = ({
    tenantId,
    tenantName,
    onNavigateBack,
    onEditNote,
    highlightNoteId,
    onNavigateToExchange
}: TenantNotesProps) => {
    const { userProfile } = useAuth()
    const noteRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [highlightedId, setHighlightedId] = useState(highlightNoteId)
    const [notes, setNotes] = useState<Note[]>([])
    const [workers, setWorkers] = useState<Worker[]>([])
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [hasMore, setHasMore] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Server-side filtered results
    const [serverFilteredNotes, setServerFilteredNotes] = useState<Note[]>([])
    const [filteredHasMore, setFilteredHasMore] = useState(false)
    const [filteredLoading, setFilteredLoading] = useState(false)
    const [loadingMoreFiltered, setLoadingMoreFiltered] = useState(false)

    // Filter states
    const [keyword, setKeyword] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
    const [selectedWorkers, setSelectedWorkers] = useState<string[]>([])
    const [principalOnly, setPrincipalOnly] = useState(false)
    const [showFilters, setShowFilters] = useState(true)
    const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null)
    const [selectedTypes, setSelectedTypes] = useState<string[]>([])

    const isFiltered = !!(keyword.trim() || startDate || endDate || selectedWorkers.length > 0 || selectedTypes.length > 0)

    const displayedNotes = useMemo(() => {
        if (isFiltered) return serverFilteredNotes
        return [...notes].sort((a, b) => {
            const da = new Date(a.note_date).getTime()
            const db = new Date(b.note_date).getTime()
            return sortOrder === 'desc' ? db - da : da - db
        })
    }, [isFiltered, serverFilteredNotes, notes, sortOrder])

    const processNotesData = async (notesData: any[]): Promise<Note[]> => {
        const uniqueWorkerIds = new Set<string>()
        for (const note of notesData) {
            if (note.worker_id) uniqueWorkerIds.add(note.worker_id)
            if (note.colleague_ids) note.colleague_ids.forEach((id: string) => uniqueWorkerIds.add(id))
        }

        const workerCache = new Map<string, string>()
        if (uniqueWorkerIds.size > 0) {
            const { data: workersData } = await supabase
                .from('workers')
                .select('id, name')
                .in('id', Array.from(uniqueWorkerIds))
            workersData?.forEach(w => { if (w.name) workerCache.set(w.id, w.name) })
        }

        return notesData.map(note => ({
            id: note.id,
            note_date: note.note_date,
            note: note.note,
            comment: note.comment,
            worker_name: note.worker_id ? (workerCache.get(note.worker_id) || 'Inconnu') : 'Inconnu',
            colleague_names: note.colleague_ids
                ? note.colleague_ids.map((id: string) => workerCache.get(id)).filter((n): n is string => !!n)
                : [],
            created_at: note.created_at || note.note_date,
            linked_exchange_id: note.linked_exchange_id || null,
            type: note.type || null,
        }))
    }

    const fetchNotes = async (offset: number = 0, limit: number = NOTES_PER_PAGE, targetNoteId?: string) => {
        try {
            if (offset === 0) setLoading(true)
            else setLoadingMore(true)
            setError(null)

            const { data: notesData, error: notesError } = await supabase
                .from('notes')
                .select('*')
                .eq('beneficiary_id', tenantId)
                .is('deleted_dt', null)
                .order('note_date', { ascending: false })
                .range(offset, offset + limit - 1)

            if (notesError) throw notesError

            const processedNotes = await processNotesData(notesData || [])

            setNotes(prev => offset === 0 ? processedNotes : [...prev, ...processedNotes])
            const pageHasMore = (notesData?.length ?? 0) === limit
            setHasMore(pageHasMore)

            if (targetNoteId && !processedNotes.find(n => n.id === targetNoteId) && pageHasMore) {
                await fetchNotes(offset + limit, NOTES_PER_PAGE, targetNoteId)
            }
        } catch (err) {
            console.error('Error fetching notes:', err)
            setError('Erreur lors du chargement des notes')
        } finally {
            if (offset === 0) setLoading(false)
            else setLoadingMore(false)
        }
    }

    const fetchFilteredNotes = async (offset: number = 0) => {
        try {
            if (offset === 0) setFilteredLoading(true)
            else setLoadingMoreFiltered(true)

            let query = supabase
                .from('notes')
                .select('*')
                .eq('beneficiary_id', tenantId)
                .is('deleted_dt', null)
                .order('note_date', { ascending: sortOrder === 'asc' })
                .range(offset, offset + FILTERED_NOTES_PER_PAGE - 1)

            const kw = keyword.trim().replace(/\u0027/g, "\u2019")
            if (kw) {
                query = query.or(`note.ilike.%${kw}%,comment.ilike.%${kw}%`)
            }
            if (startDate) query = query.gte('note_date', startDate)
            if (endDate) query = query.lte('note_date', endDate)
            if (selectedTypes.length > 0) {
                const normalized = selectedTypes.map(t => t.replace(/\u0027/g, "\u2019"))
                query = query.in('type', normalized)
            }

            if (selectedWorkers.length > 0) {
                const selectedIds = workers
                    .filter(w => selectedWorkers.includes(w.name))
                    .map(w => w.id)
                if (selectedIds.length > 0) {
                    if (principalOnly) {
                        query = query.in('worker_id', selectedIds)
                    } else {
                        query = query.or(`worker_id.in.(${selectedIds.join(',')}),colleague_ids.ov.{${selectedIds.join(',')}}`)
                    }
                }
            }

            const { data, error: fetchError } = await query
            if (fetchError) throw fetchError

            const processed = await processNotesData(data || [])

            if (offset === 0) {
                setServerFilteredNotes(processed)
            } else {
                setServerFilteredNotes(prev => [...prev, ...processed])
            }
            setFilteredHasMore((data?.length ?? 0) === FILTERED_NOTES_PER_PAGE)
        } catch (err) {
            console.error('Error fetching filtered notes:', err)
        } finally {
            if (offset === 0) setFilteredLoading(false)
            else setLoadingMoreFiltered(false)
        }
    }

    useEffect(() => {
        if (userProfile && tenantId) {
            fetchNotes(0, NOTES_PER_PAGE, highlightNoteId)
        }
    }, [userProfile, tenantId])

    useEffect(() => {
        if (userProfile && tenantId && showFilters && workers.length === 0) {
            supabase.from('workers').select('id, name').order('name').then(({ data }) => {
                if (data) setWorkers(data.filter(w => w.name !== null).map(w => ({ id: w.id, name: w.name! })))
            })
        }
    }, [userProfile, tenantId, showFilters])

    // Trigger server-side fetch when any filter changes
    useEffect(() => {
        if (!userProfile || !tenantId) return
        if (!isFiltered) {
            // On leaving filtered mode: reload notes with a page big enough to cover what was visible
            const prevCount = serverFilteredNotes.length
            if (hasMore && prevCount > notes.length) {
                fetchNotes(0, Math.max(NOTES_PER_PAGE, prevCount))
            }
            if (serverFilteredNotes.length > 0) setServerFilteredNotes([])
            if (filteredHasMore) setFilteredHasMore(false)
            return
        }

        if (debounceRef.current) clearTimeout(debounceRef.current)
        const delay = keyword.trim() ? 400 : 0
        debounceRef.current = setTimeout(() => {
            fetchFilteredNotes(0)
        }, delay)

        return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
    }, [keyword, startDate, endDate, selectedWorkers, principalOnly, selectedTypes, sortOrder, isFiltered, userProfile, tenantId])

    // Scroll to and highlight the target note once the list is rendered
    useEffect(() => {
        if (!highlightedId) return
        const el = noteRefs.current[highlightedId]
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            const timer = setTimeout(() => setHighlightedId(undefined), 3000)
            return () => clearTimeout(timer)
        }
    }, [displayedNotes, highlightedId])

    const formatDate = (dateString: string) => {
        const date = new Date(dateString)
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    }

    const handleEditNote = (note: Note) => {
        onEditNote(note.id, {
            id: note.id,
            locataire: tenantName,
            note: note.note,
            type: note.type,
            comment: note.comment,
            date: note.note_date,
            worker_name: note.worker_name,
            colleague_names: note.colleague_names
        })
    }

    const handleDeleteNote = async (noteId: string) => {
        const { error } = await supabase
            .from('notes')
            .update({ deleted_dt: new Date().toISOString() })
            .eq('id', noteId)
        if (error) {
            console.error('Error deleting note:', error)
            return
        }
        setNotes(prev => prev.filter(n => n.id !== noteId))
        setServerFilteredNotes(prev => prev.filter(n => n.id !== noteId))
        setDeletingNoteId(null)
    }

    const loadMoreNotes = () => {
        fetchNotes(notes.length)
    }

    const loadMoreFilteredNotes = () => {
        fetchFilteredNotes(serverFilteredNotes.length)
    }

    const clearFilters = () => {
        setKeyword('')
        setStartDate('')
        setEndDate('')
        setSelectedWorkers([])
        setSelectedTypes([])
        setPrincipalOnly(false)
        setSortOrder('desc')
    }

    const getUniqueWorkerNames = () => {
        const allWorkers = new Set<string>()
        notes.forEach(note => {
            allWorkers.add(note.worker_name)
            note.colleague_names.forEach(colleague => allWorkers.add(colleague))
        })
        return Array.from(allWorkers).filter(name => name !== 'Inconnu').sort()
    }

    if (!userProfile) {
        return <div className="flex justify-center items-center min-h-screen">Chargement...</div>
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader title={`Notes de ${tenantName}`} icon={<FileText size={20} />} onBack={onNavigateBack} />
            <div className="max-w-4xl mx-auto py-6 px-4">

                {/* Filters Section */}
                <div className="bg-white rounded-lg shadow mb-6 p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                            <Filter className="mr-2" size={20} />
                            Filtres
                        </h2>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowFilters(!showFilters)}
                                className="flex items-center gap-1.5"
                            >
                                {showFilters ? <EyeOff size={14} /> : <Eye size={14} />}
                                <span className="hidden sm:inline">{showFilters ? 'Masquer' : 'Afficher'} les filtres</span>
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={clearFilters}
                                className="flex items-center gap-1.5"
                            >
                                <RotateCcw size={14} />
                                <span className="hidden sm:inline">Réinitialiser</span>
                            </Button>
                        </div>
                    </div>

                    {showFilters && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Keyword Search */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Mots-clés
                                </label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                                    <input
                                        type="text"
                                        value={keyword}
                                        onChange={(e) => setKeyword(e.target.value)}
                                        placeholder="Rechercher dans les notes..."
                                        className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            {/* Date Range */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Date de début
                                </label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Date de fin
                                </label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Worker Filter */}
                            <div className="md:col-span-2">
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Travailleurs
                                    </label>
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id="principalOnly"
                                            checked={principalOnly}
                                            onChange={(e) => setPrincipalOnly(e.target.checked)}
                                            className="mr-2"
                                        />
                                        <label htmlFor="principalOnly" className="text-sm text-gray-600">
                                            Principal uniquement
                                        </label>
                                    </div>
                                </div>
                                <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2 bg-white">
                                    {getUniqueWorkerNames().map(workerName => (
                                        <div key={workerName} className="flex items-center mb-1">
                                            <input
                                                type="checkbox"
                                                id={`worker-${workerName}`}
                                                checked={selectedWorkers.includes(workerName)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedWorkers(prev => [...prev, workerName])
                                                    } else {
                                                        setSelectedWorkers(prev => prev.filter(w => w !== workerName))
                                                    }
                                                }}
                                                className="mr-2"
                                            />
                                            <label htmlFor={`worker-${workerName}`} className="text-sm text-gray-700">
                                                {workerName}
                                            </label>
                                        </div>
                                    ))}
                                    {getUniqueWorkerNames().length === 0 && (
                                        <div className="text-sm text-gray-500 text-center py-2">
                                            Aucun travailleur trouvé
                                        </div>
                                    )}
                                </div>
                                {selectedWorkers.length > 0 && (
                                    <div className="mt-1 text-xs text-blue-600">
                                        {selectedWorkers.length} travailleur{selectedWorkers.length > 1 ? 's' : ''} sélectionné{selectedWorkers.length > 1 ? 's' : ''}
                                        {principalOnly ? ' (principal uniquement)' : ' (principal et présents)'}
                                    </div>
                                )}
                            </div>

                            {/* Type filter */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Type de note
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {NOTE_TYPES.map(t => {
                                        const active = selectedTypes.includes(t)
                                        return (
                                            <button
                                                key={t}
                                                type="button"
                                                onClick={() => setSelectedTypes(prev =>
                                                    active ? [] : [t]
                                                )}
                                                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active
                                                    ? TYPE_COLORS[t] + ' border-transparent'
                                                    : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400'
                                                }`}
                                            >
                                                {capitalizeFirst(t)}
                                            </button>
                                        )
                                    })}
                                </div>
                                {selectedTypes.length > 0 && (
                                    <p className="mt-1 text-xs text-blue-600">
                                        {selectedTypes.length} type{selectedTypes.length > 1 ? 's' : ''} sélectionné{selectedTypes.length > 1 ? 's' : ''}
                                    </p>
                                )}
                            </div>

                            {/* Sort Order */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Tri chronologique
                                </label>
                                <div className="flex">
                                    <Button
                                        variant={sortOrder === 'desc' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => setSortOrder('desc')}
                                        className="flex-1 mr-1"
                                    >
                                        <SortDesc size={16} className="mr-1" />
                                        Récent
                                    </Button>
                                    <Button
                                        variant={sortOrder === 'asc' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => setSortOrder('asc')}
                                        className="flex-1 ml-1"
                                    >
                                        <SortAsc size={16} className="mr-1" />
                                        Ancien
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Results Count */}
                    <div className="mt-4 text-sm text-gray-600 flex items-center gap-2">
                        {isFiltered && filteredLoading ? (
                            <span className="flex items-center gap-1.5">
                                <span className="animate-spin inline-block w-3 h-3 border border-blue-500 border-t-transparent rounded-full" />
                                Recherche en cours…
                            </span>
                        ) : (
                            <>
                                {displayedNotes.length} note{displayedNotes.length !== 1 ? 's' : ''} trouvée{displayedNotes.length !== 1 ? 's' : ''}
                                {isFiltered && <span className="text-blue-500 text-xs">(filtrage serveur · {FILTERED_NOTES_PER_PAGE}/page)</span>}
                            </>
                        )}
                    </div>
                </div>

                {loading && (
                    <div className="flex justify-center pt-20">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-4 text-gray-600">Chargement des notes...</p>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                        <div className="text-red-800">{error}</div>
                    </div>
                )}

                {!loading && !error && (
                    <div className="space-y-6">
                        {isFiltered && filteredLoading ? (
                            <div className="flex justify-center py-12">
                                <div className="text-center">
                                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                                    <p className="mt-3 text-gray-500 text-sm">Chargement des résultats filtrés…</p>
                                </div>
                            </div>
                        ) : displayedNotes.length === 0 ? (
                            <div className="bg-white rounded-lg shadow p-8 text-center">
                                <FileText className="mx-auto mb-4 text-gray-400" size={48} />
                                <p className="text-gray-500">
                                    {notes.length === 0
                                        ? "Aucune note trouvée pour ce locataire"
                                        : "Aucune note ne correspond aux filtres sélectionnés"
                                    }
                                </p>
                            </div>
                        ) : (
                            displayedNotes.map((note) => (
                                <div
                                    key={note.id}
                                    ref={el => { noteRefs.current[note.id] = el }}
                                    className={`bg-white rounded-lg shadow p-6 transition-all duration-700 ${note.id === highlightedId ? 'ring-2 ring-blue-400 bg-blue-50' : ''}`}
                                >
                                    <div className="mb-3">
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                            <div className="flex items-center flex-wrap gap-1 text-sm min-w-0">
                                                <span className="text-blue-600 font-medium flex items-center">
                                                    <User size={14} className="mr-1 shrink-0" />
                                                    {note.worker_name}
                                                </span>
                                                {note.colleague_names.length > 0 && (
                                                    <>
                                                        <span className="text-gray-400 text-xs">avec</span>
                                                        {note.colleague_names.map((colleague, index) => (
                                                            <span key={index} className="text-yellow-600 font-medium text-sm">
                                                                {colleague}{index < note.colleague_names.length - 1 ? ',' : ''}
                                                            </span>
                                                        ))}
                                                    </>
                                                )}
                                            </div>
                                            {!IS_DEMO_MODE && (
                                            <div className="flex items-center gap-1 shrink-0">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleEditNote(note)}
                                                    className="h-7 px-2"
                                                >
                                                    <Edit size={14} className="sm:mr-1" />
                                                    <span className="hidden sm:inline text-xs">Modifier</span>
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200"
                                                    onClick={() => setDeletingNoteId(note.id)}
                                                    title="Supprimer"
                                                >
                                                    <Trash2 size={14} />
                                                </Button>
                                            </div>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-end gap-2">
                                            {note.type && (
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[note.type.replace(/\u2019/g, "\u0027")] ?? 'bg-gray-100 text-gray-600'}`}>
                                                    {capitalizeFirst(note.type)}
                                                </span>
                                            )}
                                            <span className="text-xs text-green-600 flex items-center">
                                                <Calendar size={12} className="mr-1" />
                                                {formatDate(note.note_date)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="border-t border-gray-200 pt-4">
                                        <div
                                            className="prose max-w-none text-gray-700 leading-relaxed"
                                            dangerouslySetInnerHTML={{ __html: note.note }}
                                        />
                                    </div>

                                    {note.comment && (
                                        <div className="border-t border-gray-100 pt-4 mt-4">
                                            <p className="text-sm font-medium text-gray-600 mb-2">Commentaire additionnel :</p>
                                            <p className="text-sm text-gray-600 italic">{note.comment}</p>
                                        </div>
                                    )}

                                    {note.linked_exchange_id && onNavigateToExchange && (
                                        <div className="border-t border-gray-100 pt-3 mt-4">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => onNavigateToExchange(note.linked_exchange_id!, tenantId)}
                                                className="text-sm"
                                            >
                                                Voir l'échange lié
                                            </Button>
                                        </div>
                                    )}

                                    <div className="border-t border-gray-100 pt-2 mt-4">
                                        <p className="text-xs text-gray-400">
                                            Créé le {formatDate(note.created_at)}
                                        </p>
                                    </div>
                                </div>
                            ))
                        )}

                        {!isFiltered && hasMore && (
                            <div className="text-center pt-4">
                                <Button variant="outline" onClick={loadMoreNotes} disabled={loadingMore}>
                                    {loadingMore ? 'Chargement...' : 'Charger plus ancien'}
                                </Button>
                            </div>
                        )}

                        {isFiltered && filteredHasMore && !filteredLoading && (
                            <div className="text-center pt-4">
                                <Button variant="outline" onClick={loadMoreFilteredNotes} disabled={loadingMoreFiltered}>
                                    {loadingMoreFiltered ? 'Chargement...' : `Charger ${FILTERED_NOTES_PER_PAGE} suivants`}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {deletingNoteId && (
                <DeleteConfirmDialog
                    onConfirm={() => handleDeleteNote(deletingNoteId)}
                    onCancel={() => setDeletingNoteId(null)}
                />
            )}
        </div>
    )
}
